/**
 * StageListEditor - Shared layout component for editing pipeline/lifecycle stages.
 *
 * Provides a consistent drag-and-drop reorderable stage list used across:
 * - Settings sidebar (StagesSlideout)
 * - Proposal pipeline customization (PipelineCustomizationRenderer)
 * - Create pipeline slideout (CreatePipelineSlideout)
 *
 * Base columns: drag handle, name, color, delete.
 * Additional columns (e.g. probability, progress bar) are passed via the
 * `columns` prop so the component has no domain-specific knowledge.
 */

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InfoCircle, Plus, XClose } from '@untitledui/icons';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import { ColorSelect } from '@/lib/crm/utils/colors/ColorSelect';
import { cx } from '@/utils/cx';

// =============================================================================
// Public types
// =============================================================================

/** Minimal stage shape required by the editor. Consumers extend as needed. */
export interface StageItem {
  id: string;
  name: string;
  color?: string;
  /** Opaque bag — custom columns read from this via renderCell */
  [key: string]: unknown;
}

/** A custom column rendered between color and delete. */
export interface StageColumn<T extends StageItem = StageItem> {
  /** Unique key for React reconciliation + header alignment */
  id: string;
  /** Column header label (empty string for no label) */
  header: string;
  /** Tailwind width class for both header and cell (e.g. 'w-16') */
  width: string;
  /** Header text alignment */
  align?: 'left' | 'center' | 'right';
  /** Render the cell for a given stage */
  renderCell: (stage: T, opts: { disabled: boolean }) => ReactNode;
  /** Render content shown in the drag overlay for this column (optional) */
  renderDragPreview?: (stage: T) => ReactNode;
}

export interface StageValidationErrors {
  [stageId: string]: { name?: string; [key: string]: string | undefined };
}

export interface StageListEditorProps<T extends StageItem = StageItem> {
  /** The stages to display */
  stages: T[];
  /** Custom columns rendered between color and delete */
  columns?: StageColumn<T>[];
  /**
   * Optional detail content for a row (e.g. a read-only stage description).
   * When it returns non-null for a stage, a disclosure chevron is shown
   * inline with the name; the content renders below the name when expanded.
   */
  renderRowDetail?: (stage: T) => ReactNode;
  /**
   * Optional always-visible content rendered full-width directly beneath the
   * row's controls, inside the same draggable element. Unlike
   * `renderRowDetail` there is no disclosure chevron — use this for fields
   * that should always be editable (e.g. a stage description).
   */
  renderRowFooter?: (stage: T) => ReactNode;

  // ── Layout variants ──────────────────────────────────────────────────
  /** Wrap each row in a bordered card (default: false) */
  bordered?: boolean;
  /** Show column headers above the list (default: false) */
  showColumnHeaders?: boolean;
  /** Label for the stage-name column header (default: 'Stage') */
  nameHeader?: string;

  // ── Callbacks ────────────────────────────────────────────────────────
  onNameChange: (id: string, name: string) => void;
  onNameBlur?: (id: string) => void;
  onColorChange: (id: string, color: string | null) => void;
  onRemove: (id: string) => void;
  /** Called after drag-and-drop reorder with the new array */
  onReorder: (stages: T[]) => void;
  /** Called when "+ Add stage" is clicked */
  onAdd: () => void;

  // ── State ────────────────────────────────────────────────────────────
  disabled?: boolean;
  /** Whether delete is allowed — boolean or per-stage function */
  canDelete?: boolean | ((id: string) => boolean);
  /** Hide the delete button entirely (e.g. no permission) */
  hideDelete?: boolean;
  /** Hide the add button entirely (e.g. no permission) */
  hideAdd?: boolean;
  /** Disable the add button independently of `disabled` (e.g. saving state) */
  disableAdd?: boolean;
  /** Per-stage validation errors keyed by stage id */
  validationErrors?: StageValidationErrors;
  /** Stage id currently checking usage (shows spinner instead of delete) */
  checkingUsageFor?: string | null;
  /** Per-stage usage errors keyed by stage id */
  usageErrors?: Record<string, string>;
  /** Aria-live announcement text (managed by parent) */
  announcement?: string;
  /** Generate a data-form-field attribute value for a stage's name input wrapper */
  formFieldName?: (stageId: string) => string;
}

// =============================================================================
// StageRow - Individual row
// =============================================================================

interface StageRowInternalProps<T extends StageItem> {
  stage: T;
  columns: StageColumn<T>[];
  renderRowDetail?: (stage: T) => ReactNode;
  renderRowFooter?: (stage: T) => ReactNode;
  bordered: boolean;
  onNameChange: (id: string, name: string) => void;
  onNameBlur?: (id: string) => void;
  onColorChange: (id: string, color: string | null) => void;
  onRemove: (id: string) => void;
  disabled: boolean;
  canDelete: boolean;
  hideDelete: boolean;
  nameError?: string;
  usageError?: string;
  checkingUsage: boolean;
  formFieldName?: string;
  dragHandleProps: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
  } | null;
}

function StageRowInternal<T extends StageItem>({
  stage,
  columns,
  renderRowDetail,
  renderRowFooter,
  bordered,
  onNameChange,
  onNameBlur,
  onColorChange,
  onRemove,
  disabled,
  canDelete,
  hideDelete,
  nameError,
  usageError,
  checkingUsage,
  formFieldName,
  dragHandleProps,
}: StageRowInternalProps<T>) {
  const displayError = usageError || nameError;

  const detail = renderRowDetail?.(stage) ?? null;
  const footer = renderRowFooter?.(stage) ?? null;
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const toggleDetail = useCallback(() => setIsDetailOpen(open => !open), []);

  const handleNameChange = useCallback(
    (value: string) => onNameChange(stage.id, value),
    [onNameChange, stage.id]
  );

  const handleBorderedNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onNameChange(stage.id, e.target.value),
    [onNameChange, stage.id]
  );

  const handleNameBlur = useCallback(
    () => onNameBlur?.(stage.id),
    [onNameBlur, stage.id]
  );

  const handleColorChange = useCallback(
    (color: string | null) => onColorChange(stage.id, color),
    [onColorChange, stage.id]
  );

  const handleRemove = useCallback(
    () => onRemove(stage.id),
    [onRemove, stage.id]
  );

  return (
    <div className='space-y-1'>
      <div
        className={cx(
          'group flex gap-2',
          footer !== null ? 'items-start' : 'items-center',
          bordered && 'px-3 py-2.5 rounded-lg border transition-colors',
          bordered && displayError
            ? 'border-error-300 bg-error-50'
            : bordered &&
                'border-border-secondary bg-bg-primary hover:border-gray-300'
        )}
      >
        {/* Drag handle */}
        {dragHandleProps ? (
          <button
            type='button'
            {...dragHandleProps.listeners}
            {...dragHandleProps.attributes}
            disabled={disabled}
            className={cx(
              'flex items-center justify-center w-6 h-6 shrink-0 touch-none rounded',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
              disabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-grab active:cursor-grabbing',
              bordered &&
                'opacity-40 group-hover:opacity-100 transition-opacity'
            )}
            aria-label='Drag to reorder'
          >
            <GripVertical className='w-4 h-4 text-fg-quaternary' />
          </button>
        ) : (
          <div className='w-6 h-6 shrink-0' />
        )}

        {/* Content column — controls row, then optional full-width footer */}
        <div className='flex-1 min-w-0 space-y-1.5'>
          <div className='flex items-center gap-2'>
            {/* Name input */}
            <div className='flex-1 min-w-0' data-form-field={formFieldName}>
              <div className='flex items-center gap-1'>
                <div className='flex-1 min-w-0'>
                  {bordered ? (
                    <input
                      type='text'
                      value={stage.name}
                      onChange={handleBorderedNameChange}
                      onBlur={handleNameBlur}
                      disabled={disabled}
                      placeholder='Stage name'
                      className={cx(
                        'w-full bg-transparent text-sm font-medium text-fg-primary',
                        'placeholder:text-fg-quaternary border-0 outline-none focus:ring-0 p-0',
                        disabled && 'cursor-not-allowed opacity-50'
                      )}
                    />
                  ) : (
                    <InputGroup
                      aria-label='Stage name'
                      value={stage.name}
                      onChange={handleNameChange}
                      onBlur={handleNameBlur}
                      isDisabled={disabled}
                      isInvalid={!!nameError}
                    >
                      <InputBase placeholder='Stage name' />
                    </InputGroup>
                  )}
                </div>
                {detail !== null && (
                  <button
                    type='button'
                    onClick={toggleDetail}
                    disabled={disabled}
                    aria-expanded={isDetailOpen}
                    aria-label={`${isDetailOpen ? 'Hide' : 'Show'} details for ${stage.name || 'stage'}`}
                    className={cx(
                      'flex items-center justify-center w-5 h-5 shrink-0 rounded',
                      disabled
                        ? 'cursor-not-allowed opacity-50 text-fg-quaternary'
                        : cx(
                            'cursor-pointer',
                            isDetailOpen
                              ? 'text-fg-secondary'
                              : 'text-fg-quaternary hover:text-fg-secondary'
                          ),
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                    )}
                  >
                    <InfoCircle className='w-3.5 h-3.5' />
                  </button>
                )}
              </div>
              {bordered && displayError && (
                <p className='text-xs text-error-600 mt-0.5'>{displayError}</p>
              )}
              {detail !== null && (
                <div
                  // `inert` keeps the collapse animation while preventing keyboard
                  // focus, screen-reader narration, and pointer events on the
                  // hidden description editor when the row is closed.
                  inert={!isDetailOpen}
                  aria-hidden={!isDetailOpen}
                  className={cx(
                    'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
                    isDetailOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className='overflow-hidden'>{detail}</div>
                </div>
              )}
            </div>

            {/* Color picker — fixed width to align with column header */}
            <div className='shrink-0 w-[60px]'>
              <ColorSelect
                aria-label='Stage color'
                value={stage.color ?? null}
                onChange={handleColorChange}
                isDisabled={disabled}
                placeholder='Color'
                compact
              />
            </div>

            {/* Custom columns */}
            {columns.map(col => (
              <div key={col.id} className={cx(col.width, 'shrink-0')}>
                {col.renderCell(stage, { disabled })}
              </div>
            ))}
          </div>

          {/* Full-width footer (e.g. stage description) on its own row */}
          {footer !== null && <div className='w-full'>{footer}</div>}
        </div>

        {/* Delete button */}
        {!hideDelete &&
          (checkingUsage ? (
            <div className='flex items-center justify-center w-8 h-8 shrink-0'>
              <SmallSpinner />
            </div>
          ) : (
            <button
              type='button'
              onClick={handleRemove}
              disabled={disabled || !canDelete}
              className={cx(
                'flex items-center justify-center w-8 h-8 shrink-0 rounded',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                disabled || !canDelete
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:text-error cursor-pointer',
                bordered &&
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                bordered && (!canDelete || disabled) && 'disabled:opacity-0'
              )}
              aria-label={
                canDelete
                  ? `Remove ${stage.name || 'stage'}`
                  : 'Minimum required stages reached'
              }
              title={
                canDelete
                  ? `Remove ${stage.name || 'stage'}`
                  : 'Minimum required stages reached'
              }
            >
              <XClose className='w-4 h-4' />
            </button>
          ))}
      </div>

      {/* Error display (non-bordered rows show error below) */}
      {!bordered && displayError && (
        <p className='text-sm text-error-600 ml-8' role='alert'>
          {displayError}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// SortableStageRow - DnD wrapper
// =============================================================================

interface SortableStageRowInternalProps<T extends StageItem> extends Omit<
  StageRowInternalProps<T>,
  'dragHandleProps'
> {
  id: string;
}

function SortableStageRow<T extends StageItem>({
  id,
  ...props
}: SortableStageRowInternalProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: props.disabled });

  const style = useMemo<CSSProperties>(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition]
  );

  const dragHandleProps = useMemo(
    () => ({ listeners, attributes }),
    [listeners, attributes]
  );

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style}>
        <div className='border-2 border-dashed border-border-secondary rounded-lg opacity-40'>
          <div className='invisible'>
            <StageRowInternal {...props} dragHandleProps={null} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <StageRowInternal {...props} dragHandleProps={dragHandleProps} />
    </div>
  );
}

// =============================================================================
// DragOverlay preview
// =============================================================================

interface DragPreviewProps<T extends StageItem> {
  stage: T;
  columns: StageColumn<T>[];
}

function DragPreview<T extends StageItem>({
  stage,
  columns,
}: DragPreviewProps<T>) {
  return (
    <div className='bg-bg-primary border border-border-primary rounded-lg shadow-lg px-3 py-2'>
      <div className='flex items-center gap-2'>
        <GripVertical className='w-4 h-4 text-fg-quaternary' />
        <span className='text-sm text-fg-primary flex-1'>{stage.name}</span>
        {columns.map(col =>
          col.renderDragPreview ? (
            <span key={col.id} className={col.width}>
              {col.renderDragPreview(stage)}
            </span>
          ) : null
        )}
      </div>
    </div>
  );
}

// =============================================================================
// SmallSpinner
// =============================================================================

function SmallSpinner() {
  return (
    <svg
      className='animate-spin size-5 text-fg-quaternary'
      viewBox='0 0 24 24'
      fill='none'
    >
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke='currentColor'
        strokeWidth='4'
      />
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      />
    </svg>
  );
}

// =============================================================================
// Module-scope constants
// =============================================================================

const EMPTY_COLUMNS: StageColumn<StageItem>[] = [];

const ALIGN_CLASSES = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

// =============================================================================
// StageListEditor - Main export
// =============================================================================

export function StageListEditor<T extends StageItem>({
  stages,
  columns: columnsProp,
  renderRowDetail,
  renderRowFooter,
  bordered = false,
  showColumnHeaders = false,
  nameHeader = 'Stage',
  onNameChange,
  onNameBlur,
  onColorChange,
  onRemove,
  onReorder,
  onAdd,
  disabled = false,
  canDelete = true,
  hideDelete = false,
  hideAdd = false,
  disableAdd,
  validationErrors,
  checkingUsageFor,
  usageErrors,
  announcement,
  formFieldName,
}: StageListEditorProps<T>) {
  const columns = columnsProp ?? EMPTY_COLUMNS;
  const [activeId, setActiveId] = useState<string | null>(null);
  const announcementRef = useRef('');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeStage = useMemo(
    () => (activeId ? stages.find(s => s.id === activeId) : undefined),
    [activeId, stages]
  );

  const sortableIds = useMemo(() => stages.map(s => s.id), [stages]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = stages.findIndex(s => s.id === active.id);
      const newIndex = stages.findIndex(s => s.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(stages, oldIndex, newIndex));
      }
    },
    [stages, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  // Resolve canDelete per stage
  const resolveCanDelete = useCallback(
    (id: string) => {
      if (typeof canDelete === 'function') {
        return canDelete(id);
      }
      return canDelete;
    },
    [canDelete]
  );

  // Track announcement for aria-live
  if (announcement !== undefined) {
    announcementRef.current = announcement;
  }

  return (
    <div className='space-y-3'>
      {/* Aria-live region for announcements */}
      <div aria-live='polite' className='sr-only'>
        {announcementRef.current}
      </div>

      {/* Column headers — padding matches bordered row's px-3 */}
      {showColumnHeaders && (
        <div
          className={cx(
            'flex items-center gap-2 text-xs font-medium text-fg-quaternary uppercase tracking-wider',
            bordered && 'px-3'
          )}
        >
          {/* drag handle spacer */}
          <span className='w-6 shrink-0' />
          {/* name */}
          <span className='flex-1'>{nameHeader}</span>
          {/* color — matches w-[60px] on the row's color picker wrapper */}
          <span className='shrink-0 w-[60px]'>Color</span>
          {/* custom columns */}
          {columns.map(col => (
            <span
              key={col.id}
              className={cx(
                col.width,
                'shrink-0',
                ALIGN_CLASSES[col.align ?? 'left']
              )}
            >
              {col.header}
            </span>
          ))}
          {/* delete spacer */}
          {!hideDelete && <span className='w-8 shrink-0' />}
        </div>
      )}

      {/* Stage list with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          <div className={bordered ? 'space-y-1.5' : 'space-y-3'}>
            {stages.map(stage => (
              <SortableStageRow<T>
                key={stage.id}
                id={stage.id}
                stage={stage}
                columns={columns}
                renderRowDetail={renderRowDetail}
                renderRowFooter={renderRowFooter}
                bordered={bordered}
                onNameChange={onNameChange}
                onNameBlur={onNameBlur}
                onColorChange={onColorChange}
                onRemove={onRemove}
                disabled={disabled}
                canDelete={resolveCanDelete(stage.id)}
                hideDelete={hideDelete}
                nameError={validationErrors?.[stage.id]?.name}
                usageError={usageErrors?.[stage.id]}
                formFieldName={formFieldName?.(stage.id)}
                checkingUsage={checkingUsageFor === stage.id}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeStage ? (
            <div className='opacity-90 shadow-lg rounded-lg'>
              <DragPreview stage={activeStage} columns={columns} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Add stage button */}
      {!hideAdd && (
        <button
          type='button'
          onClick={onAdd}
          disabled={disableAdd ?? disabled}
          className={cx(
            'flex items-center gap-1.5 text-sm font-medium transition-colors',
            'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            (disableAdd ?? disabled)
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer',
            bordered ? 'pl-3' : 'pl-8'
          )}
        >
          <Plus className='w-4 h-4' />
          Add stage
        </button>
      )}
    </div>
  );
}
